import {
	createContext,
	type ComponentType,
	type Context,
	type ReactNode,
	type Ref,
	type RefObject,
} from 'react';
import type { AdminAgentSetupSelection } from './types';

export type AdminAgentSetupTriggerProps = {
	selection: AdminAgentSetupSelection;
	children: (props: { ref: Ref<HTMLButtonElement>; isDisabled?: boolean }) => ReactNode;
};

export type AdminAgentLearnMoreTriggerProps = {
	// `ref` is widened to `HTMLElement` to match the entry point triggers hosts build on, which
	// cannot know what element they will be attached to. The placement narrows it when binding,
	// since it is the side that knows it renders a button.
	//
	// `aria-haspopup` is surfaced because the host owns the dialog and therefore knows that one
	// exists. The placement cannot assume it: a host may answer "learn more" with a navigation.
	children: (props: { ref: RefObject<HTMLElement>; 'aria-haspopup'?: 'dialog' }) => ReactNode;
};

/**
 * Host-supplied interaction behavior. The placement always renders the controls themselves -- it
 * owns their copy, layout and analytics -- and a host attaches behavior by binding the `ref` it is
 * handed. Passing components rather than callbacks is what lets hosts use ref-binding triggers such
 * as entry point modal and panel triggers, which must be attached before the click to preload.
 *
 * Each entry is independent: supplying one does not require supplying the other. Where a trigger is
 * absent the placement falls back to its own callback-based behavior.
 */
export type AdminAgentHostTriggers = {
	Setup?: ComponentType<AdminAgentSetupTriggerProps> | null;
	LearnMore?: ComponentType<AdminAgentLearnMoreTriggerProps> | null;
};

export const AdminAgentHostTriggersContext: Context<AdminAgentHostTriggers> =
	createContext<AdminAgentHostTriggers>({});
